import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, MapPin, Ruler, TrendingUp, GitCompareArrows, Check, Heart, Share2, Map as MapIcon, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import api from "../lib/api";
import { useCompare } from "../lib/compare";
import { useFavourites } from "../lib/favourites";
import PropertyMap from "../components/PropertyMap";

const CURR_LABEL = { INR: "₹", USD: "$", AED: "AED " };
function fmtPrice(inr, curr) {
  const fx = curr === "USD" ? 1 / 84 : curr === "AED" ? 1 / 22.9 : 1;
  const v = inr * fx;
  if (v >= 10000000 && curr === "INR") return `${CURR_LABEL[curr]}${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 1000000) return `${CURR_LABEL[curr]}${(v / 1000000).toFixed(2)}M`;
  if (v >= 100000) return `${CURR_LABEL[curr]}${(v / 100000).toFixed(2)}L`;
  return `${CURR_LABEL[curr]}${v.toFixed(0)}`;
}
const TAG_COLOR = {
  "Verified Clear-Title": "text-[#3E5B41] border-[#3E5B41]",
  "High Yield 9%": "text-[#D4AF37] border-[#D4AF37]",
  "Bank Auction Flip": "text-[#7A1C1C] border-[#7A1C1C]",
  "45% Below Market": "text-[#7A1C1C] border-[#7A1C1C]",
  "JV Ready": "text-[#D4AF37] border-[#D4AF37]",
  "Pre-Leased": "text-[#3E5B41] border-[#3E5B41]",
};

export default function Properties({ currency }) {
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(sp.get("asset_type") || "All");
  const [clearOnly, setClearOnly] = useState(sp.get("clear_title") === "true");
  const { toggle, isSelected, selected } = useCompare();
  const { toggleFav, isFav } = useFavourites();
  const [viewMode, setViewMode] = useState("grid");

  const shareProperty = async (p) => {
    const url = `${window.location.origin}/properties/${p.id}`;
    const data = { title: `${p.title} — BBE`, text: `${p.title} in ${p.location}. ${fmtPrice(p.price_inr, "INR")}`, url };
    if (navigator.share) {
      try { await navigator.share(data); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); } catch { toast.error("Failed to copy link"); }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (filter !== "All") params.set("type", filter);
    // Pass through URL search params from omnibox
    const category = sp.get("category");
    const location = sp.get("location");
    const priceRange = sp.get("price_range");
    const clearTitle = sp.get("clear_title");
    if (category) params.set("category", category);
    if (location) params.set("location", location);
    if (priceRange) params.set("price_range", priceRange);
    if (clearTitle) params.set("clear_title", clearTitle);

    api.get(`/properties?${params.toString()}`).then((r) => setItems(r.data)).catch(() => {});
  }, [filter, sp]);

  const visible = items.filter((p) => !clearOnly || p.tags.includes("Verified Clear-Title"));

  return (
    <div className="pt-[112px]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="eyebrow mb-6 flex items-center gap-3"><span className="accent-dot" /> Properties & Land · Bengaluru Portfolio</div>
        <h1 className="display-1 max-w-6xl" data-testid="properties-title">
          <MaskedLines lines={["A curated shelf,", "not a public listing."]} />
        </h1>
        <p className="mt-10 max-w-2xl text-[15px] leading-[1.8] text-[#EDE6D3]/75">
          Every parcel, villa, floor and distressed asset on this page has been title-audited, feasibility-modelled and priced against a live comparables set. Nothing here appears on a public portal.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-20" data-testid="properties-list">
        <div className="flex flex-wrap gap-2 items-center border-b border-[#EDE6D3]/10 pb-6">
          {["All", "Villa", "Commercial", "Distressed", "Land"].map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={`prop-filter-${t.toLowerCase()}`}
              className={`text-[11px] tracking-[2px] uppercase px-4 py-2 border transition-colors ${filter === t ? "bg-[#EDE6D3] text-[#0A2342] border-[#EDE6D3]" : "border-[#EDE6D3]/20 hover:border-[#D4AF37]"}`}
            >
              {t}
            </button>
          ))}
          <label className="ml-2 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={clearOnly} onChange={(e) => setClearOnly(e.target.checked)} className="accent-[#D4AF37]" data-testid="prop-clear-toggle" />
            <span className="text-[11px] tracking-[2px] uppercase">Clear-Title Only</span>
          </label>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50">{visible.length} listings</div>
            <div className="flex border border-[#EDE6D3]/20">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 transition-colors ${viewMode === "grid" ? "bg-[#EDE6D3] text-[#0A2342]" : "text-[#EDE6D3]/60 hover:text-[#D4AF37]"}`}
                data-testid="view-grid"
              >
                <LayoutGrid size={14} />
              </button>
              <button
                onClick={() => setViewMode("map")}
                className={`p-2 transition-colors ${viewMode === "map" ? "bg-[#EDE6D3] text-[#0A2342]" : "text-[#EDE6D3]/60 hover:text-[#D4AF37]"}`}
                data-testid="view-map"
              >
                <MapIcon size={14} />
              </button>
            </div>
          </div>
        </div>

        {viewMode === "map" && (
          <div className="mt-10">
            <PropertyMap />
          </div>
        )}

        {viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10 pb-24">
          {visible.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.05}>
              <div className="card-sand p-4 group relative" data-testid={`prop-card-${p.id}`}>
                {/* Action Buttons */}
                <div className="absolute top-6 right-6 z-10 flex gap-2">
                  <button
                    onClick={() => shareProperty(p)}
                    className="w-8 h-8 flex items-center justify-center border bg-[#0A2342]/80 border-[#EDE6D3]/30 text-[#EDE6D3]/70 hover:border-[#D4AF37] transition-all"
                    data-testid={`share-${p.id}`}
                    title="Share property"
                  >
                    <Share2 size={14} />
                  </button>
                  <button
                    onClick={() => toggleFav(p.id)}
                    className={`w-8 h-8 flex items-center justify-center border transition-all ${
                      isFav(p.id)
                        ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A2342]"
                        : "bg-[#0A2342]/80 border-[#EDE6D3]/30 text-[#EDE6D3]/70 hover:border-[#D4AF37]"
                    }`}
                    data-testid={`fav-toggle-${p.id}`}
                    title={isFav(p.id) ? "Remove from shortlist" : "Save to shortlist"}
                  >
                    <Heart size={14} className={isFav(p.id) ? "fill-[#0A2342]" : ""} />
                  </button>
                  <button
                    onClick={() => toggle(p)}
                    className={`w-8 h-8 flex items-center justify-center border transition-all ${
                      isSelected(p.id)
                        ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A2342]"
                        : "bg-[#0A2342]/80 border-[#EDE6D3]/30 text-[#EDE6D3]/70 hover:border-[#D4AF37]"
                    } ${selected.length >= 3 && !isSelected(p.id) ? "opacity-30 cursor-not-allowed" : ""}`}
                    disabled={selected.length >= 3 && !isSelected(p.id)}
                    data-testid={`compare-toggle-${p.id}`}
                    title={isSelected(p.id) ? "Remove from comparison" : "Add to comparison"}
                  >
                    {isSelected(p.id) ? <Check size={14} /> : <GitCompareArrows size={14} />}
                  </button>
                </div>

                <Link to={`/properties/${p.id}`} className="block">
                  <div className="relative a-4-5 overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2 max-w-[75%]">
                      {p.tags.map((t) => (
                        <span key={t} className={`text-[10px] tracking-[2px] uppercase px-2.5 py-1 border bg-[#0A2342]/95 ${TAG_COLOR[t] || "text-[#EDE6D3] border-[#EDE6D3]"}`}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="p-4 pt-6">
                    <div className="flex items-center justify-between text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/60">
                      <span>{p.type}</span>
                      <span>{p.status}</span>
                    </div>
                    <h3 className="font-serif text-[24px] md:text-[26px] leading-tight mt-2">{p.title}</h3>
                    <div className="mt-2 flex items-center gap-2 text-[13px] text-[#EDE6D3]/70"><MapPin size={13} /> {p.location}</div>
                    <div className="mt-5 grid grid-cols-3 gap-2 text-[11px] text-[#EDE6D3]/75">
                      <div><div className="eyebrow text-[#EDE6D3]/40">Price</div><div className="font-serif text-[17px] mt-1">{fmtPrice(p.price_inr, currency)}</div></div>
                      <div><div className="eyebrow text-[#EDE6D3]/40 flex items-center gap-1"><Ruler size={10} /> Area</div><div className="font-serif text-[17px] mt-1">{p.area_sqft.toLocaleString()}<span className="text-[10px] ml-1">sqft</span></div></div>
                      <div><div className="eyebrow text-[#EDE6D3]/40 flex items-center gap-1"><TrendingUp size={10} /> Yield</div><div className="font-serif text-[17px] mt-1">{p.yield_pct ? `${p.yield_pct}%` : "—"}</div></div>
                    </div>
                  </div>
                </Link>
                <div className="px-4 pb-4">
                  <div className="flex items-center justify-between border-t border-[#EDE6D3]/15 pt-4">
                    <a
                      href={`https://wa.me/919900186556?text=${encodeURIComponent(`Hi BBE, I'm interested in "${p.title}" (${p.type}) in ${p.location}. Could you share more details?`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 text-[11px] tracking-[1.5px] uppercase text-[#25D366] hover:text-[#128C7E] transition-colors"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`whatsapp-${p.id}`}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                      WhatsApp
                    </a>
                    <Link to={`/properties/${p.id}`} className="flex items-center gap-2">
                      <span className="text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/60">View Dossier</span>
                      <div className="border border-[#EDE6D3]/30 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37] transition-colors p-2"><ArrowUpRight size={16} /></div>
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        )}
      </section>
    </div>
  );
}
